import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import {
  PaginationEllipsis,
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { createPaginationWithEllipsis } from '@//utils/pagination';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import { facetParamsToPath, sanitizeParams } from '@//utils/vehicles-search/utils';

const SCROLL_DURATION_MS = 300;

const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

const smoothScrollTo = (targetY: number) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo({ top: targetY });
    return;
  }
  const startY = window.scrollY;
  const distance = targetY - startY;
  if (distance === 0) return;
  const startTime = performance.now();

  const step = (now: number) => {
    const progress = Math.min((now - startTime) / SCROLL_DURATION_MS, 1);
    window.scrollTo({ top: startY + distance * easeOutCubic(progress) });
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

const scrollToUtilityBar = () => {
  const target = document.getElementById('search-utility-bar');
  if (!target) return;
  const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
  smoothScrollTo(target.getBoundingClientRect().top + window.scrollY - offset);
};

const resolveHref = (rootUrl: string, q?: string) => {
  const params = sanitizeParams(new URLSearchParams(q), { keepOffset: true });
  const hasOffset = params.has('offset');
  const hasQ = params.has('q');
  if (hasOffset || hasQ) {
    return `${rootUrl}${params.toString() ? `?${params}` : ''}`;
  }

  const pretty = facetParamsToPath(params);

  const paramsAsString = params.toString();
  const href = pretty
    ? `${rootUrl}/${pretty}`
    : paramsAsString
      ? `?${paramsAsString}`
      : window.location.pathname;

  return href;
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, queryString: string) => {
      e.preventDefault();
      e.stopPropagation();
      updateUrl(sanitizeParams(queryString, { keepOffset: true }));

      (e.target as HTMLElement).blur();

      scrollToUtilityBar();
      document.getElementById('search-results')?.focus({ preventScroll: true });
    },
    [updateUrl],
  );

  const prevQuery = pagination?.previousPage?.query;

  const nextQuery = pagination?.nextPage?.query;

  const paginationItems = useMemo(
    () => createPaginationWithEllipsis(pagination?.pages || []),
    [pagination?.pages],
  );

  if (!pagination) {
    return null;
  }

  return (
    <StyledPagination testId="search-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prevQuery ? resolveHref(prevQuery) : undefined}
        testId="search-pagination-prev"
        onClick={prevQuery ? (e) => onClick(e, prevQuery) : undefined}
      />
      {paginationItems.map((item, index) => {
        if (item.isEllipsis) {
          return (
            <PaginationEllipsis
              key={`ellipsis-${index}`}
              testId={`search-pagination-ellipsis-${index}`}
            />
          );
        }

        return (
          <PaginationLink
            key={item.displayName}
            href={resolveHref(rootUrl, item.query!)}
            onClick={(e) => onClick(e, item.query!)}
            title={t('goTo.title', { page: item.displayName })}
            testId={`search-pagination-page-${item.displayName}`}
            isActive={item.selected}
          >
            {item.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={nextQuery ? resolveHref(rootUrl, nextQuery) : undefined}
        testId="search-pagination-next"
        onClick={nextQuery ? (e) => onClick(e, nextQuery) : undefined}
      />
    </StyledPagination>
  );
};

export default Pagination;
